<script lang="ts" setup>
import ProjectsDropDown, {
  type ProjectsDropDownSelectedProject,
} from '@/components/common/projects/ProjectsDropDown.vue';
import CESGRolesSelector, {
  type CESGRolesSelectorProps,
} from '@/components/common/roles/CESGRolesSelector.vue';
import {
  PhBookOpen,
  PhChat,
  PhCheck,
  PhPencilSimple,
  PhX,
} from '@phosphor-icons/vue';
import { PermissionType, ReviewPermissionType } from 'cesg-shared/dist/models';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PermissionModal, {
  type PermissionModalAction,
  type PermissionModalProps,
} from '../permission-modal/PermissionModal.vue';

interface Projects {
  id: string;
  name: string;
  number: string;
  status?: string;
  permissions: {
    ata: string;
    forecast: string;
    invoice: string;
    overview: string;
    budget: string;
    files: string;
    review: string;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  projects: Projects[];
}

export interface UserModalDisableProps {
  disable?: {
    save?: boolean;
    inputs?: {
      name?: boolean;
      email?: boolean;
      role?: boolean;
    };
  };
}

export interface UserModalHideProps {
  hide?: {
    projects?: boolean;
  };
}

export interface UserModalViewProps
  extends UserModalDisableProps,
    UserModalHideProps {
  data: {
    availableRoles?: CESGRolesSelectorProps['data']['availableRoles'];
  };
  permissionModal?: PermissionModalProps['data'] | null;
}

export type UserModalAction =
  | { type: 'close' }
  | { type: 'cancel' }
  | { type: 'save' }
  | { type: 'requestAddProject'; project: ProjectsDropDownSelectedProject }
  | { type: 'requestEditProjectPermissions'; projectId: string }
  | { type: 'permissionModalAction'; action: PermissionModalAction }
  | { type: 'removeProject'; projectId: string };

export type UserModalEmits = {
  (event: 'action', payload: UserModalAction): void;
};

const { t } = useI18n();

const props = defineProps<UserModalViewProps>();
const userModel = defineModel<User>('user', { required: true });
const emit = defineEmits<UserModalEmits>();

const permissionTypeIcon: Record<string, typeof PhX> = {
  [PermissionType.DISABLED]: PhX,
  [PermissionType.VIEW]: PhBookOpen,
  [PermissionType.COMMENT]: PhChat,
  [PermissionType.EDIT]: PhPencilSimple,
};

type PermissionKey = Exclude<
  keyof NonNullable<User['projects'][number]['permissions']>,
  'review'
>;

const standardPermissionKeys: PermissionKey[] = [
  'overview',
  'budget',
  'forecast',
  'ata',
  'invoice',
];

const legendItems: { key: PermissionKey | 'review'; labelKey: string }[] = [
  { key: 'overview', labelKey: 'permissionModalView.resources.overview' },
  { key: 'budget', labelKey: 'permissionModalView.resources.budget' },
  { key: 'forecast', labelKey: 'permissionModalView.resources.forecast' },
  { key: 'ata', labelKey: 'permissionModalView.resources.ata' },
  { key: 'invoice', labelKey: 'permissionModalView.resources.invoice' },
  { key: 'review', labelKey: 'permissionModalView.review' },
];

const hoveredProjectId = ref<string | null>(null);

const hoveredProject = computed(
  () =>
    userModel.value.projects.find((p) => p.id === hoveredProjectId.value) ??
    null,
);

const legendIconFor = (key: PermissionKey | 'review') => {
  const p = hoveredProject.value;
  if (!p) return null;
  if (key === 'review') {
    return p.permissions.review === ReviewPermissionType.ALLOWED
      ? PhCheck
      : PhX;
  }
  return permissionTypeIcon[p.permissions[key]] ?? PhX;
};

const legendIconDisabled = (key: PermissionKey | 'review') => {
  const p = hoveredProject.value;
  if (!p) return false;
  if (key === 'review')
    return p.permissions.review !== ReviewPermissionType.ALLOWED;
  return p.permissions[key] === PermissionType.DISABLED;
};
</script>

<template>
  <div class="modal is-active" role="dialog" aria-modal="true">
    <div
      class="modal-background"
      @click="emit('action', { type: 'close' })"
    ></div>

    <div class="modal-card user-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ t('usersModalView.title') }}</p>
        <button
          class="delete"
          aria-label="close"
          type="button"
          @click="emit('action', { type: 'close' })"
        ></button>
      </header>

      <section class="modal-card-body">
        <div class="field">
          <label class="label">{{ t('name') }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="userModel.name"
              :disabled="props.disable?.inputs?.name"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ t('email') }}</label>
          <div class="control">
            <input
              class="input"
              type="email"
              v-model="userModel.email"
              :disabled="props.disable?.inputs?.email"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ t('phone') }}</label>
          <div class="control">
            <input class="input" type="tel" v-model="userModel.phone" />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ t('role') }}</label>
          <div class="control">
            <CESGRolesSelector
              v-model:role="userModel.role"
              :disabled="props.disable?.inputs?.role"
              :data="{
                role: userModel.role,
                availableRoles: props.data.availableRoles,
              }"
            />
          </div>
        </div>

        <div class="field" v-if="!hide?.projects">
          <label class="label">{{ t('project') }}</label>
          <div class="projects-controls">
            <ProjectsDropDown
              class="project-dropdown"
              :data="{
                placeholder: t('usersModalView.projectDropDown'),
                excludeProjectIds: userModel.projects.map((p) => p.id),
                includeArchived: true,
                teleport: true,
              }"
              @action="
                emit('action', {
                  type: 'requestAddProject',
                  project: $event.project,
                })
              "
            />
            <div class="project-status-legend" aria-hidden="true">
              <span class="legend-pill is-aktiv">{{ t('active') }}</span>
              <span class="legend-pill is-avsultat">{{ t('finished') }}</span>
            </div>
          </div>
          <p class="perm-icon-legend" aria-hidden="true">
            <span
              v-for="(item, i) in legendItems"
              :key="item.key"
              class="legend-item"
            >
              <span
                class="legend-label"
                :class="{
                  'is-disabled': legendIconDisabled(item.key),
                  'is-inactive': !hoveredProject,
                }"
                >{{ t(item.labelKey) }}</span
              >
              <component
                :is="legendIconFor(item.key) ?? PhX"
                :size="12"
                weight="bold"
                class="legend-perm-icon"
                :class="{
                  'is-disabled': legendIconDisabled(item.key),
                  'is-invisible': !hoveredProject,
                }"
              />
              <span v-if="i < legendItems.length - 1" class="legend-sep"
                >|</span
              >
            </span>
          </p>
          <div class="project-pills" v-if="userModel.projects.length > 0">
            <span
              class="project-pill"
              :class="{
                'is-closed': ['Avslutad', 'Avslutat'].includes(
                  project.status ?? '',
                ),
                'is-open': !['Avslutad', 'Avslutat'].includes(
                  project.status ?? '',
                ),
              }"
              v-for="project in userModel.projects"
              :key="project.id"
              @mouseenter="hoveredProjectId = project.id"
              @mouseleave="hoveredProjectId = null"
            >
              <div class="pill-content">
                <button
                  type="button"
                  class="project-name"
                  @click="
                    emit('action', {
                      type: 'requestEditProjectPermissions',
                      projectId: project.id,
                    })
                  "
                >
                  {{ `${project.number} - ${project.name}` }}
                </button>
                <span class="perm-icons" aria-hidden="true">
                  <component
                    v-for="key in standardPermissionKeys"
                    :key="key"
                    :is="permissionTypeIcon[project.permissions[key]] ?? PhX"
                    :size="13"
                    weight="bold"
                    class="perm-icon"
                    :class="{
                      'is-disabled':
                        project.permissions[key] === PermissionType.DISABLED,
                    }"
                  />
                  <component
                    :is="
                      project.permissions.review ===
                      ReviewPermissionType.ALLOWED
                        ? PhCheck
                        : PhX
                    "
                    :size="13"
                    weight="bold"
                    class="perm-icon"
                    :class="{
                      'is-disabled':
                        project.permissions.review !==
                        ReviewPermissionType.ALLOWED,
                    }"
                  />
                </span>
              </div>
              <button
                type="button"
                class="remove-project"
                @click="
                  emit('action', {
                    type: 'removeProject',
                    projectId: project.id,
                  })
                "
                aria-label="Remove project"
              >
                <PhX weight="bold" />
              </button>
            </span>
          </div>
          <p class="help" v-else>{{ t('usersModalView.noProjects') }}</p>
        </div>
      </section>

      <footer class="modal-card-foot actions">
        <button
          type="button"
          class="button is-light"
          @click="emit('action', { type: 'cancel' })"
        >
          {{ t('cancel') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          @click="emit('action', { type: 'save' })"
          :disabled="props.disable?.save"
        >
          {{ t('save') }}
        </button>
      </footer>
    </div>
  </div>
  <PermissionModal
    v-if="props.permissionModal"
    :data="props.permissionModal"
    @action="emit('action', { type: 'permissionModalAction', action: $event })"
  />
</template>

<style scoped lang="scss">
.user-modal-card {
  width: min(760px, 92vw);
  max-height: calc(100dvh - 4rem);
  display: flex;
  flex-direction: column;
}

.modal-card-body {
  position: relative;
  z-index: 2;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.perm-icon-legend {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.6rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.legend-label {
  color: var(--primary, #485fc7);

  &.is-disabled {
    color: #9ca3af;
  }

  &.is-inactive {
    color: #9ca3af;
  }
}

.legend-perm-icon {
  color: var(--primary, #485fc7);
  flex-shrink: 0;

  &.is-disabled {
    color: #9ca3af;
  }

  &.is-invisible {
    visibility: hidden;
  }
}

.legend-sep {
  color: #d1d5db;
}

.project-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.projects-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.project-dropdown {
  flex: 1 1 auto;
  min-width: 0;
}

.project-status-legend {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

.legend-pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid #cfd3dc;
  border-radius: 999px;
  padding: 0.38rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.2;
  font-weight: 600;
}

.legend-pill.is-aktiv {
  border-color: var(--primary, #485fc7);
  background: rgba(72, 95, 199, 0.14);
  color: #1f2937;
}

.legend-pill.is-avsultat {
  border-color: #cfd3dc;
  background: #f2f4f7;
  color: #374151;
}

@media (max-width: 768px) {
  .projects-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .project-dropdown {
    flex: 1 1 auto;
    max-width: 100%;
  }

  .project-status-legend {
    margin-left: 0;
  }
}

.project-pill {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #cfd3dc;
  border-radius: 999px;
  padding: 0.38rem 0.62rem 0.38rem 0.82rem;
  background: #f9fafb;
}

.project-pill.is-open {
  border-color: var(--primary, #485fc7);
  background: rgba(72, 95, 199, 0.14);
  color: #1f2937;
}

.project-pill.is-closed {
  border-color: #cfd3dc;
  background: #f9fafb;
}

.perm-icons {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.perm-icon {
  color: var(--primary, #485fc7);
  flex-shrink: 0;

  &.is-disabled {
    color: #d1d5db;
  }
}

.project-pill.is-open .perm-icon {
  color: var(--primary, #485fc7);

  &.is-disabled {
    color: rgba(72, 95, 199, 0.3);
  }
}

.pill-top {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.pill-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
}

.project-name {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 0.94rem;
  line-height: 1.3;
  font-weight: 600;
  text-align: left;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
}

.project-pill.is-open .project-name {
  color: #1f2937;
}

.remove-project {
  border: 0;
  border-radius: 999px;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  font-size: 1.05rem;
  line-height: 1;
  background: #eceff3;
  color: #374151;
}

.remove-project:hover {
  background: #dfe4ea;
}

.project-pill.is-open .remove-project {
  background: #c9d8f8;
  color: #1f2937;
}

.project-pill.is-open .remove-project:hover {
  background: #b8cbf5;
}
</style>
